import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Org } from '@figureit/shared';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthProvider';

/**
 * First-run wizard. Surfaces optional setup steps so a brand-new user gets
 * something useful out of the CRM in under two minutes:
 *   1. Welcome
 *   2. Quarterly goals
 *   3. Invite teammates
 *   4. Create the first lead
 *
 * Every step is skippable. Hitting "Finish" or closing marks the user as
 * onboarded so it never reappears.
 */
export function OnboardingWizard({ onClose }: { onClose: () => void }) {
  const { me, currentOrgId, refresh } = useAuth();
  const qc = useQueryClient();
  const [step, setStep] = useState(0);

  const role = me?.organizations.find((o) => o.id === currentOrgId)?.role;
  const canEditOrg = role === 'owner' || role === 'admin';

  const finish = useMutation({
    mutationFn: () => api('/api/auth/me/onboarded', { method: 'POST' }),
    onSuccess: async () => {
      await refresh();
      onClose();
    },
  });

  // Esc closes (and finishes).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') finish.mutate();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [finish]);

  const next = () => setStep((s) => Math.min(STEPS - 1, s + 1));
  const prev = () => setStep((s) => Math.max(0, s - 1));
  const STEPS = canEditOrg ? 4 : 3; // skip the goals step for non-admins

  return (
    <div className="fixed inset-0 bg-black/70 z-[80] flex items-end sm:items-center justify-center sm:p-6">
      <div className="bg-surface border border-border rounded-t-2xl sm:rounded-xl w-full sm:w-[640px] max-w-full max-h-[92dvh] sm:max-h-[90vh] overflow-y-auto overscroll-contain animate-[slideUp_0.2s_ease]">
        <div className="p-4 sm:p-6 pb-[max(1rem,env(safe-area-inset-bottom))] sm:pb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex gap-1">
              {Array.from({ length: STEPS }).map((_, i) => (
                <span
                  key={i}
                  className={'h-1.5 w-8 rounded ' + (i <= step ? 'bg-accent' : 'bg-surface2')}
                />
              ))}
            </div>
            <button onClick={() => finish.mutate()} className="text-text3 hover:text-text text-xs">
              Skip & close ✕
            </button>
          </div>

          {step === 0 && <WelcomeStep me={me} onNext={next} />}
          {step === (canEditOrg ? 1 : -1) && canEditOrg && (
            <GoalsStep
              orgId={currentOrgId!}
              onDone={() => {
                qc.invalidateQueries({ queryKey: ['org'] });
                qc.invalidateQueries({ queryKey: ['report'] });
                next();
              }}
              onSkip={next}
            />
          )}
          {step === (canEditOrg ? 2 : 1) && (
            <InviteStep
              canInvite={canEditOrg}
              onDone={() => {
                qc.invalidateQueries({ queryKey: ['invites'] });
                next();
              }}
              onSkip={next}
            />
          )}
          {step === (canEditOrg ? 3 : 2) && (
            <FirstLeadStep onDone={() => finish.mutate()} onSkip={() => finish.mutate()} />
          )}

          {/* Nav row */}
          {step > 0 && (
            <div className="mt-6 flex justify-between">
              <button className="btn btn-ghost" onClick={prev}>
                ← Back
              </button>
              <span className="text-xs text-text3 self-center">
                Step {step + 1} of {STEPS}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function WelcomeStep({ me, onNext }: { me: ReturnType<typeof useAuth>['me']; onNext: () => void }) {
  return (
    <div>
      <h2 className="text-2xl font-bold mb-1">
        Welcome{me?.name ? `, ${me.name.split(' ')[0]}` : ''} 👋
      </h2>
      <p className="text-text2 mb-4">
        Your CRM is ready. We've pre-seeded a sales pipeline with sensible defaults — feel free to
        customize it later under <strong>Settings</strong>.
      </p>
      <ul className="text-sm text-text2 space-y-2 mb-6">
        <li>📊 8 pipeline stages with default close probabilities</li>
        <li>📞 Activity types with summary templates so logging is one click</li>
        <li>🏷 Tags ready to use (hot · inbound · referral)</li>
        <li>📧 Daily digest email opted in (turn off in Settings → My notifications)</li>
      </ul>
      <p className="text-sm text-text3 mb-4">
        We'll spend the next 2 minutes setting up the things that need <em>your</em> decisions. You
        can skip any step.
      </p>
      <button className="btn btn-primary" onClick={onNext}>
        Let's go →
      </button>
    </div>
  );
}

function GoalsStep({
  orgId,
  onDone,
  onSkip,
}: {
  orgId: string;
  onDone: () => void;
  onSkip: () => void;
}) {
  const [deals, setDeals] = useState('');
  const [revenue, setRevenue] = useState('');
  const save = useMutation({
    mutationFn: () =>
      api('/api/org', {
        method: 'PATCH',
        body: JSON.stringify({
          quarterlyGoalDeals: deals === '' ? null : Number(deals),
          quarterlyGoalRevenue: revenue === '' ? null : Number(revenue),
        } satisfies Partial<Org>),
      }),
    onSuccess: onDone,
  });
  return (
    <div>
      <h2 className="text-xl font-bold mb-1">Set a quarterly goal</h2>
      <p className="text-text3 text-sm mb-4">
        Optional — but it makes the Overview way more useful. We'll show progress bars and the
        Reports tab compares your weighted forecast to your goal.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
        <label className="block">
          <span className="text-xs text-text2 block mb-1">Deals to win this quarter</span>
          <input
            type="number"
            inputMode="numeric"
            value={deals}
            onChange={(e) => setDeals(e.target.value)}
            placeholder="e.g. 10"
          />
        </label>
        <label className="block">
          <span className="text-xs text-text2 block mb-1">Revenue target (₪/yr)</span>
          <input
            type="number"
            inputMode="numeric"
            value={revenue}
            onChange={(e) => setRevenue(e.target.value)}
            placeholder="e.g. 1500000"
          />
        </label>
      </div>
      <div className="flex gap-2">
        <button className="btn btn-secondary" onClick={onSkip}>
          Skip
        </button>
        <button
          className="btn btn-primary"
          onClick={() => save.mutate()}
          disabled={(!deals && !revenue) || save.isPending}
        >
          {save.isPending ? 'Saving…' : 'Save & continue →'}
        </button>
      </div>
    </div>
  );
}

function InviteStep({
  canInvite,
  onDone,
  onSkip,
}: {
  canInvite: boolean;
  onDone: () => void;
  onSkip: () => void;
}) {
  const [emails, setEmails] = useState('');
  const sendAll = useMutation({
    mutationFn: async () => {
      const list = emails
        .split(/[\s,;]+/)
        .map((s) => s.trim())
        .filter((s) => s.includes('@'));
      let sent = 0;
      for (const email of list) {
        try {
          await api('/api/org/invites', {
            method: 'POST',
            body: JSON.stringify({ email, role: 'member' }),
          });
          sent++;
        } catch {
          // ignore individual failures (already-invited, malformed, etc.)
        }
      }
      return sent;
    },
    onSuccess: () => onDone(),
  });
  if (!canInvite) {
    return (
      <div>
        <h2 className="text-xl font-bold mb-1">Members</h2>
        <p className="text-text3 text-sm mb-4">
          Only owners and admins can invite teammates. Ask one of yours if you need someone added.
        </p>
        <button className="btn btn-primary" onClick={onSkip}>
          Continue →
        </button>
      </div>
    );
  }
  return (
    <div>
      <h2 className="text-xl font-bold mb-1">Invite your team</h2>
      <p className="text-text3 text-sm mb-3">
        Drop a few email addresses below — one per line, or comma-separated. They'll get a link, can
        sign in with Google, and will join as members. You can change their role later in Settings.
      </p>
      <textarea
        rows={4}
        value={emails}
        onChange={(e) => setEmails(e.target.value)}
        placeholder="alice@company.com&#10;bob@company.com"
        autoCapitalize="none"
        autoCorrect="off"
        inputMode="email"
        className="mb-3"
      />
      <div className="flex gap-2">
        <button className="btn btn-secondary" onClick={onSkip}>
          Skip
        </button>
        <button
          className="btn btn-primary"
          onClick={() => sendAll.mutate()}
          disabled={!emails.trim() || sendAll.isPending}
        >
          {sendAll.isPending ? 'Sending…' : 'Send invites →'}
        </button>
      </div>
    </div>
  );
}

function FirstLeadStep({ onDone, onSkip }: { onDone: () => void; onSkip: () => void }) {
  return (
    <div>
      <h2 className="text-xl font-bold mb-1">You're all set 🎉</h2>
      <p className="text-text2 mb-4">
        Add your first lead from the <strong>+ New Lead</strong> button, or import an existing list
        (CSV/JSON) via <strong>Import</strong>.{' '}
        <span className="hidden sm:inline">
          Press <kbd className="text-[10px] bg-surface2 border border-border rounded px-1">⌘K</kbd>{' '}
          any time to jump to a lead, switch view, or run a quick action.
        </span>
        <span className="sm:hidden">
          On your phone, use the bottom bar to switch views and the 🔍 button to jump to a lead.
        </span>
      </p>
      <ul className="text-sm text-text2 space-y-1 mb-6">
        <li>
          📥 <strong>Import</strong> — bring an existing pipeline in via CSV
        </li>
        <li>
          📊 <strong>Pipeline</strong> — drag deals between stages, watch SLAs
        </li>
        <li>
          📞 On any lead, <strong>Quick log</strong> a call/email/meeting in one click
        </li>
        <li>🏆 Closing a deal? Pick a Won/Lost reason — feeds Reports</li>
      </ul>
      <div className="flex gap-2 justify-end">
        <button className="btn btn-ghost" onClick={onSkip}>
          Close
        </button>
        <button className="btn btn-primary" onClick={onDone}>
          Open the CRM →
        </button>
      </div>
    </div>
  );
}
